/**
 * A visit's FINAL due date (`visits.final_due`): the real last day the visit
 * can still be done and stay compliant, for agencies whose due date has slack
 * (Platinum's CSV due dates are a week out; the compliance deadline is two to
 * three weeks later).
 *
 * The due date stays the target: staffing, urgency and price all work toward
 * it. The final due date only matters once the due date can't be met, and then
 * it replaces the due date as the deadline (availability slots, no-show runway,
 * respawned clones).
 *
 * Stored like `scheduled_due`: 8 PM patient-local on that date. NULL means the
 * due date is firm. A final due date on or before the due date (for example
 * after the due date was pushed past it) adds nothing and is ignored.
 */
function toDate(value) {
    if (value == null) {
        return null;
    }
    const d = value instanceof Date ? value : new Date(value);
    return Number.isNaN(d.getTime()) ? null : d;
}
/** The final due date when it gives time beyond the due date, otherwise null. */
export function effectiveFinalDue(scheduledDue, finalDue) {
    const final = toDate(finalDue);
    if (final == null) {
        return null;
    }
    const due = toDate(scheduledDue);
    if (due != null && final.getTime() <= due.getTime()) {
        return null;
    }
    return final;
}
/**
 * The latest the visit can still happen: the final due date when it gives
 * extra time, otherwise the due date. Null when neither is set.
 */
export function visitDeadline(scheduledDue, finalDue) {
    return effectiveFinalDue(scheduledDue, finalDue) ?? toDate(scheduledDue);
}
/** Most days a final due date can be after the due date (per visit or as a default). */
export const FINAL_DUE_MAX_DAYS = 365;
/**
 * The final due date `days` calendar days after `dueDate` (both YYYY-MM-DD),
 * used for an agency's per-visit-type default. Null when `dueDate` isn't a
 * real date or `days` isn't a whole number from 1 to FINAL_DUE_MAX_DAYS.
 */
export function finalDueAfterDays(dueDate, days) {
    if (!Number.isInteger(days) || days < 1 || days > FINAL_DUE_MAX_DAYS) {
        return null;
    }
    const day = dueDate.slice(0, 10);
    if (!/^\d{4}-\d{2}-\d{2}$/.test(day)) {
        return null;
    }
    const ms = Date.parse(`${day}T00:00:00Z`);
    if (Number.isNaN(ms)) {
        return null;
    }
    return new Date(ms + days * 24 * 60 * 60 * 1000).toISOString().slice(0, 10);
}
/**
 * Checks a final due date as people enter it: calendar dates (YYYY-MM-DD) for
 * the due date and the final due date. Same rule for the agency portal, the
 * CSV upload and the API. Returns the message to show, or null when it's fine
 * (including no final due date at all).
 */
export function finalDueDateError(dueDate, finalDue) {
    if (!finalDue) {
        return null;
    }
    if (!dueDate) {
        return "Set a due date before adding a final due date.";
    }
    if (finalDue.slice(0, 10) <= dueDate.slice(0, 10)) {
        return "The final due date must be after the due date. Leave it empty if the due date is firm.";
    }
    return null;
}
//# sourceMappingURL=final-due.js.map